import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface UserNotification {
  id: string;
  email: string;
  name: string;
  phone: string;
  is_active: boolean;
  email_notifications_enabled: boolean;
  daily_email_enabled: boolean;
  daily_email_time: string;
  weekly_email_enabled: boolean;
  weekly_email_day: number;
  weekly_email_time: string;
  monthly_email_enabled: boolean;
  monthly_email_date: number;
  monthly_email_time: string;
  whatsapp_notifications_enabled: boolean;
  whatsapp_allowed: boolean;
  daily_whatsapp_enabled: boolean;
  daily_whatsapp_time: string;
  weekly_whatsapp_enabled: boolean;
  weekly_whatsapp_day: number;
  weekly_whatsapp_time: string;
  monthly_whatsapp_enabled: boolean;
  monthly_whatsapp_date: number;
  monthly_whatsapp_time: string;
  timezone: string;
  last_daily_notification_at: string;
  last_weekly_notification_at: string;
  last_monthly_notification_at: string;
}

interface NotificationLog {
  id: string;
  user_id: string;
  email: string;
  name: string;
  notification_type: string;
  channel: string;
  status: string;
  error_message: string;
  created_at: string;
}

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const NotificationManagement: React.FC = () => {
  const [users, setUsers] = useState<UserNotification[]>([]);
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'users' | 'logs'>('users');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UserNotification>>({});
  const [testingUser, setTestingUser] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiService.getAdminUsersNotifications();
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await apiService.getNotificationLogs({ limit: 50 });
      setLogs(response.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchLogs]);

  const handleEditUser = (user: UserNotification) => {
    setEditingUser(user.id);
    setEditForm({
      email_notifications_enabled: user.email_notifications_enabled ?? true,
      daily_email_enabled: user.daily_email_enabled ?? true,
      daily_email_time: user.daily_email_time?.slice(0, 5) || '07:00',
      weekly_email_enabled: user.weekly_email_enabled ?? true,
      weekly_email_day: user.weekly_email_day ?? 0,
      weekly_email_time: user.weekly_email_time?.slice(0, 5) || '08:00',
      monthly_email_enabled: user.monthly_email_enabled ?? true,
      monthly_email_date: user.monthly_email_date ?? 1,
      monthly_email_time: user.monthly_email_time?.slice(0, 5) || '09:00',
      whatsapp_notifications_enabled: user.whatsapp_notifications_enabled ?? false,
      daily_whatsapp_enabled: user.daily_whatsapp_enabled ?? false,
      weekly_whatsapp_enabled: user.weekly_whatsapp_enabled ?? false,
      monthly_whatsapp_enabled: user.monthly_whatsapp_enabled ?? false,
    });
  };

  const handleSaveUser = async (userId: string) => {
    try {
      await apiService.updateUserNotifications(userId, editForm);
      setMessage({ type: 'success', text: 'Notification settings updated!' });
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      setMessage({ type: 'error', text: 'Failed to update settings' });
    }
  };

  const handleTestNotification = async (userId: string, type: string, channel: string) => {
    try {
      setTestingUser(userId);
      await apiService.sendTestNotification(userId, type, channel);
      setMessage({ type: 'success', text: `Test ${type} ${channel} notification sent!` });
      await fetchLogs();
    } catch (error: any) {
      console.error('Failed to send test notification:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to send notification' });
    } finally {
      setTestingUser(null);
    }
  };

  const handleTriggerProcessing = async () => {
    try {
      await apiService.triggerNotificationProcessing();
      setMessage({ type: 'success', text: 'Notification processing triggered!' });
      await fetchLogs();
    } catch (error) {
      console.error('Failed to trigger processing:', error);
      setMessage({ type: 'error', text: 'Failed to trigger processing' });
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '-';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes} ${period}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="animate-spin" style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTop: '3px solid #667eea', borderRadius: '50%', margin: '0 auto' }}></div>
        <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading notification settings...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          borderRadius: '8px',
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setActiveView('users')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: activeView === 'users' ? 'var(--primary-color, #667eea)' : '#f3f4f6',
              color: activeView === 'users' ? 'white' : '#374151',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            👥 User Settings
          </button>
          <button
            onClick={() => setActiveView('logs')}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: activeView === 'logs' ? 'var(--primary-color, #667eea)' : '#f3f4f6',
              color: activeView === 'logs' ? 'white' : '#374151',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            📋 Notification Logs
          </button>
        </div>
        <button
          onClick={handleTriggerProcessing}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#059669',
            color: 'white',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          ⚡ Process Now
        </button>
      </div>

      {activeView === 'users' && (
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Email Enabled</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Daily</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Weekly</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Monthly</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <React.Fragment key={user.id}>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600 }}>{user.name || user.email}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</div>
                      {!user.is_active && <span style={{ fontSize: '10px', background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px' }}>Inactive</span>}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        background: user.email_notifications_enabled ? '#d1fae5' : '#fee2e2',
                        color: user.email_notifications_enabled ? '#065f46' : '#991b1b'
                      }}>
                        {user.email_notifications_enabled ? '✓ ON' : '✕ OFF'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {user.daily_email_enabled ? (
                        <div>
                          <div style={{ color: '#059669', fontWeight: 600 }}>✓</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatTime(user.daily_email_time)}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {user.weekly_email_enabled ? (
                        <div>
                          <div style={{ color: '#059669', fontWeight: 600 }}>✓</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>{dayNames[user.weekly_email_day || 0]?.slice(0, 3)} {formatTime(user.weekly_email_time)}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {user.monthly_email_enabled ? (
                        <div>
                          <div style={{ color: '#059669', fontWeight: 600 }}>✓</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>{user.monthly_email_date || 1}th {formatTime(user.monthly_email_time)}</div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleEditUser(user)}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#f3f4f6', cursor: 'pointer', marginRight: '8px' }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => setTestingUser(testingUser === user.id ? null : user.id)}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#dbeafe', cursor: 'pointer' }}
                      >
                        🧪 Test
                      </button>
                    </td>
                  </tr>
                  
                  {/* Edit Form */}
                  {editingUser === user.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '20px', background: '#f9fafb' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                          {/* Email Master Switch */}
                          <div style={{ gridColumn: 'span 3' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editForm.email_notifications_enabled}
                                onChange={(e) => setEditForm({ ...editForm, email_notifications_enabled: e.target.checked })}
                                style={{ width: '18px', height: '18px' }}
                              />
                              <span style={{ fontWeight: 600 }}>Enable Email Notifications</span>
                            </label>
                          </div>

                          {/* Daily Email */}
                          <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editForm.daily_email_enabled}
                                onChange={(e) => setEditForm({ ...editForm, daily_email_enabled: e.target.checked })}
                                style={{ width: '16px', height: '16px' }}
                              />
                              <span style={{ fontWeight: 600 }}>Daily Summary</span>
                            </label>
                            <div style={{ marginLeft: '26px' }}>
                              <label style={{ fontSize: '12px', color: '#6b7280' }}>Time:</label>
                              <input
                                type="time"
                                value={editForm.daily_email_time || '07:00'}
                                onChange={(e) => setEditForm({ ...editForm, daily_email_time: e.target.value })}
                                style={{ display: 'block', marginTop: '4px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                              />
                            </div>
                          </div>

                          {/* Weekly Email */}
                          <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editForm.weekly_email_enabled}
                                onChange={(e) => setEditForm({ ...editForm, weekly_email_enabled: e.target.checked })}
                                style={{ width: '16px', height: '16px' }}
                              />
                              <span style={{ fontWeight: 600 }}>Weekly Report</span>
                            </label>
                            <div style={{ marginLeft: '26px' }}>
                              <label style={{ fontSize: '12px', color: '#6b7280' }}>Day:</label>
                              <select
                                value={editForm.weekly_email_day ?? 0}
                                onChange={(e) => setEditForm({ ...editForm, weekly_email_day: parseInt(e.target.value) })}
                                style={{ display: 'block', marginTop: '4px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db', width: '100%' }}
                              >
                                {dayNames.map((day, idx) => (
                                  <option key={idx} value={idx}>{day}</option>
                                ))}
                              </select>
                              <label style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', display: 'block' }}>Time:</label>
                              <input
                                type="time"
                                value={editForm.weekly_email_time || '08:00'}
                                onChange={(e) => setEditForm({ ...editForm, weekly_email_time: e.target.value })}
                                style={{ display: 'block', marginTop: '4px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                              />
                            </div>
                          </div>

                          {/* Monthly Email */}
                          <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={editForm.monthly_email_enabled}
                                onChange={(e) => setEditForm({ ...editForm, monthly_email_enabled: e.target.checked })}
                                style={{ width: '16px', height: '16px' }}
                              />
                              <span style={{ fontWeight: 600 }}>Monthly Report</span>
                            </label>
                            <div style={{ marginLeft: '26px' }}>
                              <label style={{ fontSize: '12px', color: '#6b7280' }}>Date:</label>
                              <select
                                value={editForm.monthly_email_date ?? 1}
                                onChange={(e) => setEditForm({ ...editForm, monthly_email_date: parseInt(e.target.value) })}
                                style={{ display: 'block', marginTop: '4px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db', width: '100%' }}
                              >
                                {Array.from({ length: 28 }, (_, i) => i + 1).map(date => (
                                  <option key={date} value={date}>{date}{date === 1 ? 'st' : date === 2 ? 'nd' : date === 3 ? 'rd' : 'th'}</option>
                                ))}
                              </select>
                              <label style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', display: 'block' }}>Time:</label>
                              <input
                                type="time"
                                value={editForm.monthly_email_time || '09:00'}
                                onChange={(e) => setEditForm({ ...editForm, monthly_email_time: e.target.value })}
                                style={{ display: 'block', marginTop: '4px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                              />
                            </div>
                          </div>
                        </div>

                        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setEditingUser(null)}
                            style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSaveUser(user.id)}
                            style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'var(--primary-color, #667eea)', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                          >
                            Save Changes
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Test Notification Buttons */}
                  {testingUser === user.id && !editingUser && (
                    <tr>
                      <td colSpan={6} style={{ padding: '15px', background: '#eff6ff' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, color: '#1e40af' }}>Send Test Email:</span>
                          <button
                            onClick={() => handleTestNotification(user.id, 'daily', 'email')}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}
                          >
                            📋 Daily
                          </button>
                          <button
                            onClick={() => handleTestNotification(user.id, 'weekly', 'email')}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}
                          >
                            📊 Weekly
                          </button>
                          <button
                            onClick={() => handleTestNotification(user.id, 'monthly', 'email')}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer' }}
                          >
                            📈 Monthly
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeView === 'logs' && (
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>User</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Channel</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                    No notification logs yet. Send a test notification to see logs here.
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6b7280' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600 }}>{log.name || log.email}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{log.email}</div>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: log.notification_type === 'daily' ? '#dbeafe' : log.notification_type === 'weekly' ? '#f3e8ff' : '#d1fae5',
                        color: log.notification_type === 'daily' ? '#1e40af' : log.notification_type === 'weekly' ? '#6b21a8' : '#065f46'
                      }}>
                        {log.notification_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: '16px' }}>{log.channel === 'email' ? '📧' : '💬'}</span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: log.status === 'sent' ? '#d1fae5' : log.status === 'skipped' ? '#fef3c7' : '#fee2e2',
                        color: log.status === 'sent' ? '#065f46' : log.status === 'skipped' ? '#92400e' : '#991b1b'
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>
                      {log.error_message || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
